import { useEffect, useMemo } from 'react';
import { X } from 'lucide-react';

import { t } from '../lib/i18n';
import { useAppStore } from '../store';

interface ImagePreviewModalProps {
  /** Image to render — typically the post-scan-correct blob, falling back
   *  to the original upload File. Both are Blob-compatible. */
  source: Blob;
  filename: string;
  onClose: () => void;
}

export function ImagePreviewModal({ source, filename, onClose }: ImagePreviewModalProps) {
  const lang = useAppStore((s) => s.lang);

  // Single object URL for the rendered <img>, revoked on unmount. Memo
  // keyed on the blob identity so a new upload's preview doesn't reuse
  // the previous URL.
  const objectUrl = useMemo(() => URL.createObjectURL(source), [source]);
  useEffect(() => {
    return () => URL.revokeObjectURL(objectUrl);
  }, [objectUrl]);

  // Escape closes — matches the CropModal convention. No focus-trap beyond
  // the close button: this modal has a single primary action.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={filename}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={t(lang, 'crop.close')}
        title={t(lang, 'crop.close')}
        className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-900/70 text-slate-200 transition-colors hover:bg-slate-900 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60"
      >
        <X className="h-5 w-5" aria-hidden="true" />
      </button>
      <img
        src={objectUrl}
        alt={filename}
        // stopPropagation so clicking the image doesn't bubble to the
        // backdrop click-to-close handler on the parent div.
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
      />
    </div>
  );
}
